import { AsyncPipe } from '@angular/common';
import { Component, HostBinding, inject, Input, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { Amplify } from 'aws-amplify';
import { disable as disableAmplifyAnalytics } from 'aws-amplify/analytics';
import tinycolor from 'tinycolor2';
import { CONFIG } from '../../../../libs/essentials/types/src/mea-config';
import { ChatAndShopButtonComponent } from './chat-and-shop-button/chat-and-shop-button.component';
import { loadPharmacy } from './store/actions/pharmacy.actions';
import { selectChatPartnerPharmacy } from './store/selectors/pharmacy.selectors';
import { MeaState } from './store/state/mea.state';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [ChatAndShopButtonComponent, AsyncPipe],
})
export class AppComponent {
  @HostBinding('class') classes = 'chat-widget';

  @Input() shopUrl?: string;
  @Input() primaryColor?: string;

  @Input() set pharmacyId(pharmacyId: string) {
    this.store.dispatch(loadPharmacy({ pharmacyId }));
  }

  @HostBinding('style') get setColors() {
    if (!this.primaryColor) {
      return;
    }
    const styles = [
      `--ion-color-primary: ${this.primaryColor};`,
      `--ion-color-primary-rgb: ${this.hexToRgb(this.primaryColor)};`,
      `--ion-color-primary-shade: ${this.getHoverColor(this.primaryColor)};`,
      `--ion-color-primary-contrast: ${this.getContrastColor(this.primaryColor)}`,
    ];
    return styles.join(' ');
  }

  private config = inject(CONFIG);
  private store = inject<Store<MeaState>>(Store);

  pharmacy$ = this.store.select(selectChatPartnerPharmacy);

  constructor() {
    Amplify.configure(this.config.aws_config);
    disableAmplifyAnalytics();
    this.setViewportMetaTag();
  }

  private setViewportMetaTag = () => {
    let viewportMetaTag = document.querySelector('meta[name="viewport"]') as any;
    if (!viewportMetaTag) {
      viewportMetaTag = document.createElement('meta');
      viewportMetaTag.setAttribute('name', 'viewport');
      viewportMetaTag.setAttribute('content', 'width=device-width, initial-scale=1');
      document.querySelector('head')?.appendChild(viewportMetaTag);
    }

    if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i)) {
      if (viewportMetaTag.content && viewportMetaTag.content.length > 0) {
        viewportMetaTag.content += ', maximum-scale=1.0';
      } else {
        viewportMetaTag.content = 'maximum-scale=1.0';
      }
    }
  };

  private getHoverColor(colorString: string) {
    const color = tinycolor(colorString);

    return color.getBrightness() > 30 ? color.darken().toHexString() : color.brighten().toHexString();
  }

  private getContrastColor(color: string) {
    return tinycolor(color).getBrightness() > 160 ? '#000' : '#fff';
  }

  private hexToRgb(hex: string): string {
    let r = '0x00';
    let g = '0x00';
    let b = '0x00';

    if (hex.length === 4) {
      r = '0x' + hex[1] + hex[1];
      g = '0x' + hex[2] + hex[2];
      b = '0x' + hex[3] + hex[3];
    } else if (hex.length === 7) {
      r = '0x' + hex[1] + hex[2];
      g = '0x' + hex[3] + hex[4];
      b = '0x' + hex[5] + hex[6];
    }

    return `${+r}, ${+g}, ${+b}`;
  }
}
